import React, { useMemo, useState } from "react";
import { Card } from "melodies-source/Card";
import { H2 } from "melodies-source/Text";
import { Divider, useArtistContext } from "Components";
import {
  useCollection,
  useCollectionData,
} from "react-firebase-hooks/firestore";
import {
  collection,
  documentId,
  getFirestore,
  orderBy,
  Query,
  query,
  where,
} from "firebase/firestore";
import { ReleaseDetail } from "./ReleaseDetail";
import { Spinner } from "melodies-source/Spinner";
import { TotalPreSaves } from "./TotalPreSaves";
import styled from "styled-components";
import { ServiceChart } from "./ServiceChart";
import { StatusChart } from "./StatusChart";
import { SourceChart } from "./SourceChart";
import { InsufficientData } from "./InsuffcientData";
import { RecentPreSaves } from "./RecentPreSaves";
import {
  PresaveSource,
  presaveSources,
  ReleasePresave,
} from "@max/common/dist/artists/presave";
import { Ref } from "refs";
import { Select } from "melodies-source/Select";
import { Source, SourceFilter } from "./types";

const OPTION_TITLES: Record<Source, string> = {
  setfan: "Pre-Saves from This Campaign",
  setbio: "Pre-Saves from SET.Bio",
};

interface PreSavesProps {
  source: Source;
  releasesIds?: string[];
}

export const PreSaves: React.FC<PreSavesProps> = ({ source, releasesIds }) => {
  const { id: artistGroupId } = useArtistContext();
  const [selectedReleaseIndex, setSelectedReleaseIndex] = useState<number>(0);
  const [filter, setFilter] = useState<SourceFilter>("all");

  const [artistReleasesSnapshot, artistReleasesSnapshotLoading] = useCollection(
    releasesIds
      ? query(
          Ref("artist_releases"),
          where("artistGroupId", "==", artistGroupId),
          where("deletedAt", "==", null),
          where(documentId(), "in", releasesIds),
        )
      : query(
          Ref("artist_releases"),
          where("artistGroupId", "==", artistGroupId),
          where("image", "!=", null),
          where("deletedAt", "==", null),
          orderBy("releaseDate", "desc"),
        ),
  );

  const artistReleaseDoc = artistReleasesSnapshot?.docs[selectedReleaseIndex];
  const [preSaves, preSavesLoading] = useCollectionData(
    query(
      collection(getFirestore(), "release_presaves"),
      where("artistGroupId", "==", artistGroupId),
      where("releaseIds", "array-contains", artistReleaseDoc?.id || ""),
      where("timestamp", "!=", null),
    ) as Query<ReleasePresave>,
  );

  const sources = useMemo<PresaveSource[]>(() => {
    if (filter === "all") {
      return [...presaveSources];
    } else if (filter === "setfan") {
      return ["setfan", "auto"];
    } else {
      return ["setbio:takeover", "setbio:section", "auto"];
    }
  }, [filter]);

  const preSavesByFilter = useMemo(() => {
    if (!preSaves || !artistReleaseDoc?.id) {
      return [];
    }

    return preSaves.filter((preSave) =>
      sources.includes(
        preSave.completedReleases[artistReleaseDoc.id]?.source as PresaveSource,
      ),
    );
  }, [artistReleaseDoc, preSaves, sources]);

  const handleBack = () =>
    setSelectedReleaseIndex((index) => Math.max(0, index - 1));

  const handleNext = () =>
    setSelectedReleaseIndex((index) =>
      Math.min(artistReleasesSnapshot?.docs.length - 1, index + 1),
    );

  if (
    artistReleasesSnapshotLoading ||
    artistReleasesSnapshot?.docs.length === 0
  ) {
    return null;
  }

  return (
    <Card isElevated>
      <Container>
        <Title>
          <div>{/* Keep empty div for 3 columns */}</div>
          <H2>Pre-Saves</H2>
          <Select
            value={filter}
            options={[
              {
                label: "Pre-Saves from All Sources",
                value: "all",
              },
              {
                label: OPTION_TITLES[source],
                value: source,
              },
            ]}
            onChange={(v) => setFilter(v as SourceFilter)}
          />
        </Title>
        <FirstRow>
          {artistReleaseDoc && (
            <ReleaseDetail
              release={artistReleaseDoc.data()}
              releaseIndex={selectedReleaseIndex}
              releasesLength={artistReleasesSnapshot.docs.length}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {preSavesLoading ? (
            <>
              <StyledSpinner />
              <StyledSpinner />
            </>
          ) : (
            <>
              <TotalPreSaves
                preSaves={preSavesByFilter}
                releaseId={artistReleaseDoc?.id}
                release={artistReleaseDoc?.data()}
                filter={filter}
              />
              <RecentPreSaves
                preSaves={preSavesByFilter}
                releaseId={artistReleaseDoc?.id}
                filter={filter}
                source={source}
              />
            </>
          )}
        </FirstRow>
        <StyledDivider />
        <SecondRow>
          {preSavesLoading ? (
            <Spinner />
          ) : preSaves.length === 0 ? (
            <InsufficientData
              message={`This section will populate as more fans pre-save ${
                artistReleaseDoc ? "this release" : "releases"
              }`}
            />
          ) : (
            <>
              <ServiceChart preSaves={preSavesByFilter} source={source} />
              <StatusChart
                preSaves={preSavesByFilter}
                releaseId={artistReleaseDoc?.id}
                source={source}
              />
              <SourceChart
                preSaves={preSavesByFilter}
                releaseId={artistReleaseDoc?.id}
                sources={sources}
                source={source}
              />
            </>
          )}
        </SecondRow>
      </Container>
    </Card>
  );
};

const Container = styled.div`
  ${H2} {
    text-align: center;
  }
`;

const Title = styled.div`
  margin-bottom: 50px;
  display: grid;
  grid-template-columns: 300px 1fr 300px;
  align-items: center;

  ${(p) => p.theme.media.mobile} {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

const FirstRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    gap: 50px;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 40px 0;
`;

const SecondRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 40px;
  min-height: 320px;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    min-height: unset;
  }
`;

const StyledSpinner = styled(Spinner)`
  flex: 1;
  padding: 0 15px;
  min-height: 150px;
`;
